import React, { useState } from 'react';
import styled from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import { 
  FaProjectDiagram, 
  FaUsers, 
  FaCalendarAlt, 
  FaMapMarkerAlt,
  FaMoneyBillWave,
  FaChartLine,
  FaEdit,
  FaArrowLeft,
  FaDownload,
  FaEye,
  FaUserPlus,
  FaTimes,
  FaSave,
  FaCheck,
  FaExclamationTriangle,
  FaTrash,
  FaPhone,
  FaEnvelope,
  FaIdCard
} from 'react-icons/fa';
import IconWrapper from '../common/IconWrapper';

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const PageTitle = styled.h1`
  color: #141340;
  font-size: 2rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 1rem;
  
  &::after {
    content: '';
    flex: 1;
    height: 2px;
    background: linear-gradient(90deg, #F68B21 0%, transparent 100%);
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  ${({ variant }) => {
    switch (variant) {
      case 'primary':
        return `
          background: linear-gradient(135deg, #F68B21 0%, #e67c1a 100%);
          color: white;
          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 16px rgba(246, 139, 33, 0.3);
          }
        `;
      case 'secondary':
        return `
          background: white;
          color: #141340;
          border: 2px solid #e0e0e0;
          &:hover {
            border-color: #F68B21;
            color: #F68B21;
          }
        `;
      case 'danger':
        return `
          background: #dc3545;
          color: white;
          &:hover {
            background: #c82333;
            transform: translateY(-2px);
          }
        `;
      default:
        return `
          background: white;
          color: #141340;
          border: 2px solid #e0e0e0;
          &:hover {
            border-color: #F68B21;
            color: #F68B21;
          }
        `;
    }
  }}
`;

const ProjectCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border: 1px solid #e0e0e0;
  overflow: hidden;
`;

const ProjectHeader = styled.div`
  background: linear-gradient(135deg, #141340 0%, #1a1a4a 50%, #2d2d6b 100%);
  color: white;
  padding: 2rem;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 20%, rgba(246, 139, 33, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.05) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const ProjectIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 12px;
  background: linear-gradient(135deg, #F68B21 0%, #e67c1a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: white;
  margin-bottom: 1rem;
  box-shadow: 0 8px 24px rgba(246, 139, 33, 0.3);
`;

const ProjectName = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

const ProjectId = styled.p`
  font-size: 1rem;
  opacity: 0.9;
  margin-bottom: 0.5rem;
`;

const ProjectDescription = styled.p`
  font-size: 1.1rem;
  opacity: 0.9;
  margin-bottom: 0;
`;

const ProjectContent = styled.div`
  padding: 2rem;
`;

const Section = styled.div`
  margin-bottom: 2rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  color: #141340;
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #f0f0f0;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #F68B21;
  transition: all 0.3s ease;
  
  &:hover {
    background: #e9ecef;
    transform: translateX(4px);
  }
`;

const InfoIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #F68B21 0%, #e67c1a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1rem;
  flex-shrink: 0;
`;

const InfoContent = styled.div`
  flex: 1;
`;

const InfoLabel = styled.div`
  font-size: 0.8rem;
  color: #666;
  text-transform: uppercase;
  font-weight: 600;
  margin-bottom: 0.25rem;
`;

const InfoValue = styled.div`
  font-weight: 600;
  color: #141340;
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  
  ${({ status }) => {
    switch (status) {
      case 'in-progress':
        return `
          background: #fff3cd;
          color: #856404;
        `;
      case 'completed':
        return `
          background: #d4edda;
          color: #155724;
        `;
      case 'draft':
        return `
          background: #e2e3e5;
          color: #383d41;
        `;
      case 'on-hold':
        return `
          background: #f8d7da;
          color: #721c24;
        `;
      default:
        return `
          background: #e2e3e5;
          color: #383d41;
        `;
    }
  }}
`;

const ProgressSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border: 1px solid #e0e0e0;
  margin-bottom: 2rem;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 12px;
  background: #e0e0e0;
  border-radius: 6px;
  overflow: hidden;
  margin: 1rem 0;
`;

const ProgressFill = styled.div<{ percentage: number }>`
  width: ${({ percentage }) => percentage}%;
  height: 100%;
  background: linear-gradient(90deg, #F68B21 0%, #e67c1a 100%);
  transition: width 0.3s ease;
`;

const ProgressInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const ProgressLabel = styled.span`
  font-weight: 600;
  color: #141340;
`;

const ProgressPercentage = styled.span`
  font-weight: 600;
  color: #F68B21;
`;

const BudgetInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.5rem;
  font-size: 0.9rem;
  color: #666;
`;

const StaffSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border: 1px solid #e0e0e0;
  margin-bottom: 2rem;
`;

const StaffGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const StaffCard = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
  border-left: 4px solid #F68B21;
  transition: all 0.3s ease;
  
  &:hover {
    background: #e9ecef;
    transform: translateY(-2px);
  }
`;

const StaffHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
`;

const StaffAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #141340 0%, #1a1a4a 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.9rem;
`;

const StaffInfo = styled.div`
  flex: 1;
`;

const StaffName = styled.div`
  font-weight: 600;
  color: #141340;
  font-size: 0.9rem;
`;

const StaffRole = styled.div`
  font-size: 0.8rem;
  color: #666;
`;

const StaffContact = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
  font-size: 0.8rem;
  color: #666;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const AlertMessage = styled.div<{ type: 'success' | 'error' | 'warning' }>`
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  ${({ type }) => {
    switch (type) {
      case 'success':
        return `
          background: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
        `;
      case 'error':
        return `
          background: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        `;
      case 'warning':
        return `
          background: #fff3cd;
          color: #856404;
          border: 1px solid #ffeaa7;
        `;
    }
  }}
`;

const HRProjectDetail: React.FC = () => {
  const { id } = useParams();
  const [alertMessage, setAlertMessage] = useState<{ type: 'success' | 'error' | 'warning', message: string } | null>(null);

  // Mock project data
  const projectData = {
    id: 'PRJ-001',
    name: 'Road Construction Project',
    description: 'Construction of main highway connecting two major cities with modern infrastructure and safety features.',
    coordinator: 'Sarah Johnson',
    status: 'in-progress',
    budget: 5000000,
    spent: 3200000,
    startDate: '2023-01-15',
    endDate: '2024-06-30',
    location: 'Dar es Salaam - Arusha',
    type: 'Infrastructure',
    teamSize: 45,
    completionPercentage: 64,
    assignedStaff: [
      {
        name: 'John Doe',
        role: 'Supervisor',
        email: 'john.doe@example.com',
        phone: '+255 123 456 789',
        employeeId: 'EMP-001'
      },
      {
        name: 'Jane Smith',
        role: 'Project Manager',
        email: 'jane.smith@example.com',
        phone: '+255 123 456 790',
        employeeId: 'EMP-002'
      },
      {
        name: 'Mike Wilson',
        role: 'Store-Keeper (Logistics)',
        email: 'mike.wilson@example.com',
        phone: '+255 123 456 791',
        employeeId: 'EMP-003'
      },
      {
        name: 'Emily Davis',
        role: 'Cashier',
        email: 'emily.davis@example.com',
        phone: '+255 123 456 792',
        employeeId: 'EMP-004'
      }
    ]
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'TZS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStaffInitials = (name: string) => {
    const nameParts = name.split(' ');
    if (nameParts.length >= 2) {
      return `${nameParts[0][0]}${nameParts[1][0]}`;
    }
    return nameParts[0][0];
  };

  const handleRemoveStaff = async (staffId: string) => {
    if (window.confirm('Are you sure you want to remove this staff member from the project?')) {
      try {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        setAlertMessage({ type: 'success', message: 'Staff member removed from project successfully.' });
        
        // Clear alert after 3 seconds
        setTimeout(() => setAlertMessage(null), 3000);
      } catch (error) {
        setAlertMessage({ type: 'error', message: 'Failed to remove staff member. Please try again.' });
      }
    }
  };

  return (
    <Container>
      {alertMessage && (
        <AlertMessage type={alertMessage.type}>
          <IconWrapper icon={alertMessage.type === 'success' ? FaCheck : FaExclamationTriangle} />
          {alertMessage.message}
        </AlertMessage>
      )}

      <PageHeader>
        <PageTitle>
          <IconWrapper icon={FaProjectDiagram} />
          Project Details
        </PageTitle>
        <ActionButtons>
          <Button variant="secondary" as={Link} to="/hr/projects">
            <IconWrapper icon={FaArrowLeft} />
            Back to Projects
          </Button>
          <Button variant="secondary">
            <IconWrapper icon={FaDownload} />
            Export
          </Button>
                      <Button variant="primary" as={Link} to={`/hr/projects/edit/${projectData.id}`}>
            <IconWrapper icon={FaEdit} />
            Edit Project
          </Button>
        </ActionButtons>
      </PageHeader>

      <ProjectCard>
        <ProjectHeader>
          <ProjectIcon>
            <IconWrapper icon={FaProjectDiagram} />
          </ProjectIcon>
          <ProjectName>{projectData.name}</ProjectName>
          <ProjectId>{projectData.id}</ProjectId>
          <ProjectDescription>{projectData.description}</ProjectDescription>
          <StatusBadge status={projectData.status}>
            {projectData.status.replace('-', ' ')}
          </StatusBadge>
        </ProjectHeader>

        <ProjectContent>
          <ProgressSection>
            <SectionTitle>
              <IconWrapper icon={FaChartLine} />
              Project Progress
            </SectionTitle>
            <ProgressInfo>
              <ProgressLabel>Overall Progress</ProgressLabel>
              <ProgressPercentage>{projectData.completionPercentage}%</ProgressPercentage>
            </ProgressInfo>
            <ProgressBar>
              <ProgressFill percentage={projectData.completionPercentage} />
            </ProgressBar>
            <BudgetInfo>
              <span>Budget: {formatCurrency(projectData.budget)}</span>
              <span>Spent: {formatCurrency(projectData.spent)}</span>
              <span>Remaining: {formatCurrency(projectData.budget - projectData.spent)}</span>
            </BudgetInfo>
          </ProgressSection>

          <Section>
            <SectionTitle>
              <IconWrapper icon={FaProjectDiagram} />
              Project Information
            </SectionTitle>
            <InfoGrid>
              <InfoItem>
                <InfoIcon>
                  <IconWrapper icon={FaUsers} />
                </InfoIcon>
                <InfoContent>
                  
                </InfoContent>
              </InfoItem>
              
              <InfoItem>
                <InfoIcon>
                  <IconWrapper icon={FaMapMarkerAlt} />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Location</InfoLabel>
                  <InfoValue>{projectData.location}</InfoValue>
                </InfoContent>
              </InfoItem>
              
              <InfoItem>
                <InfoIcon>
                  <IconWrapper icon={FaProjectDiagram} />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Project Type</InfoLabel>
                  <InfoValue>{projectData.type}</InfoValue>
                </InfoContent>
              </InfoItem>
              
              <InfoItem>
                <InfoIcon>
                  <IconWrapper icon={FaUsers} />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Team Size</InfoLabel>
                  <InfoValue>{projectData.teamSize} members</InfoValue>
                </InfoContent>
              </InfoItem>
            </InfoGrid>
          </Section>

          <Section>
            <SectionTitle>
              <IconWrapper icon={FaCalendarAlt} />
              Timeline
            </SectionTitle>
            <InfoGrid>
              <InfoItem>
                <InfoIcon>
                  <IconWrapper icon={FaCalendarAlt} />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Start Date</InfoLabel>
                  <InfoValue>{new Date(projectData.startDate).toLocaleDateString()}</InfoValue>
                </InfoContent>
              </InfoItem>
              
              <InfoItem>
                <InfoIcon>
                  <IconWrapper icon={FaCalendarAlt} />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>End Date</InfoLabel>
                  <InfoValue>{new Date(projectData.endDate).toLocaleDateString()}</InfoValue>
                </InfoContent>
              </InfoItem>
              
              <InfoItem>
                <InfoIcon>
                  <IconWrapper icon={FaCalendarAlt} />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Duration</InfoLabel>
                  <InfoValue>18 months</InfoValue>
                </InfoContent>
              </InfoItem>
              
              <InfoItem>
                <InfoIcon>
                  <IconWrapper icon={FaCalendarAlt} />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Days Remaining</InfoLabel>
                  <InfoValue>150 days</InfoValue>
                </InfoContent>
              </InfoItem>
            </InfoGrid>
          </Section>

          <Section>
            <SectionTitle>
              <IconWrapper icon={FaMoneyBillWave} />
              Financial Information
            </SectionTitle>
            <InfoGrid>
              <InfoItem>
                <InfoIcon>
                  <IconWrapper icon={FaMoneyBillWave} />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Total Budget</InfoLabel>
                  <InfoValue>{formatCurrency(projectData.budget)}</InfoValue>
                </InfoContent>
              </InfoItem>
              
              <InfoItem>
                <InfoIcon>
                  <IconWrapper icon={FaMoneyBillWave} />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Amount Spent</InfoLabel>
                  <InfoValue>{formatCurrency(projectData.spent)}</InfoValue>
                </InfoContent>
              </InfoItem>
              
              <InfoItem>
                <InfoIcon>
                  <IconWrapper icon={FaMoneyBillWave} />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Remaining Budget</InfoLabel>
                  <InfoValue>{formatCurrency(projectData.budget - projectData.spent)}</InfoValue>
                </InfoContent>
              </InfoItem>
              
              <InfoItem>
                <InfoIcon>
                  <IconWrapper icon={FaChartLine} />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Budget Utilization</InfoLabel>
                  <InfoValue>{Math.round((projectData.spent / projectData.budget) * 100)}%</InfoValue>
                </InfoContent>
              </InfoItem>
            </InfoGrid>
          </Section>

          <StaffSection>
            <SectionTitle>
              <IconWrapper icon={FaUsers} />
              Assigned Staff ({projectData.assignedStaff.length} members)
            </SectionTitle>
            <StaffGrid>
              {projectData.assignedStaff.map((staff, index) => (
                <StaffCard key={index}>
                  <StaffHeader>
                    <StaffAvatar>
                      {getStaffInitials(staff.name)}
                    </StaffAvatar>
                    <StaffInfo>
                      <StaffName>{staff.name}</StaffName>
                      <StaffRole>{staff.role}</StaffRole>
                    </StaffInfo>
                    <Button 
                      variant="danger" 
                      onClick={() => handleRemoveStaff(staff.employeeId)}
                      style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
                    >
                      <IconWrapper icon={FaTrash} />
                    </Button>
                  </StaffHeader>
                  <StaffContact>
                    <ContactItem>
                      <IconWrapper icon={FaIdCard} style={{ fontSize: '0.7rem' }} />
                      {staff.employeeId}
                    </ContactItem>
                    <ContactItem>
                      <IconWrapper icon={FaEnvelope} style={{ fontSize: '0.7rem' }} />
                      {staff.email}
                    </ContactItem>
                    <ContactItem>
                      <IconWrapper icon={FaPhone} style={{ fontSize: '0.7rem' }} />
                      {staff.phone}
                    </ContactItem>
                  </StaffContact>
                </StaffCard>
              ))}
            </StaffGrid>
          </StaffSection>
        </ProjectContent>
      </ProjectCard>
    </Container>
  );
};

export default HRProjectDetail; 